import { useTranslations } from "next-intl";
import Link from "next/link";
import { useShoppingCart } from "use-shopping-cart";
import { formatPrice } from "@/utils/formatePrice";

import { useCheckoutForm } from "../Checkout/form";
import { DiscountType } from "@/types/discount";
import { useEcommerce } from "../EcommerceProvider/EcommerceProvider";
import {
  computeCartDiscountGrosze,
  resolveCartPromotion,
} from "@/utils/promotions";

const OrderSummary = () => {
  const { cartCount, cartDetails, totalPrice = 0 } = useShoppingCart();
  const { watch } = useCheckoutForm();
  const ecommerce = useEcommerce();

  const t = useTranslations("CartSummary");
  const tCartView = useTranslations("CartView");

  const couponDiscountValue = Number(watch("couponDiscountValue") || 0);
  const couponDiscountType = watch("couponDiscountType");

  let couponDiscountAmount = 0;
  const cartPromotion = resolveCartPromotion(
    ecommerce.promotions ?? [],
    Math.round(totalPrice * 100),
  );
  const cartPromotionDiscountAmount = cartPromotion
    ? computeCartDiscountGrosze(Math.round(totalPrice * 100), cartPromotion) /
      100
    : 0;

  if (couponDiscountType === DiscountType.PERCENT) {
    couponDiscountAmount =
      Math.floor(totalPrice * Math.max(0, couponDiscountValue)) / 100;
  } else if (
    couponDiscountType === DiscountType.FIXED ||
    couponDiscountValue > 0
  ) {
    couponDiscountAmount = couponDiscountValue;
  }

  couponDiscountAmount = Math.min(
    couponDiscountAmount,
    Math.max(0, totalPrice - cartPromotionDiscountAmount),
  );
  const discountedPrice = Math.max(
    0,
    totalPrice - cartPromotionDiscountAmount - couponDiscountAmount,
  );

  return (
    <div className="lg:max-w-2/5 w-full">
      {/* <!-- order list box --> */}
      <div className="bg-white shadow-1 rounded-[10px]">
        <div className="border-b border-gray-3 py-5 px-4 sm:px-8.5">
          <h3 className="font-medium text-xl text-dark">{t("title")}</h3>
        </div>

        <div className="pt-2.5 pb-8.5 px-4 sm:px-8.5">
          {/* <!-- title --> */}
          <div className="flex items-center justify-between py-5 border-b border-gray-3">
            <div>
              <h4 className="font-bold text-dark">{t("product")}</h4>
            </div>
            <div>
              <h4 className="font-bold text-dark text-right">
                {t("subtotal")}
              </h4>
            </div>
          </div>

          {/* <!-- product item --> */}
          {cartCount && (
            <>
              {Object.values(cartDetails ?? {}).map((product, key) => (
                <div
                  key={key}
                  className="flex items-center justify-between py-5 border-b border-gray-3"
                >
                  <div>
                    <p className="text-dark">
                      {product.name} x{product.quantity}
                      {product.discountLabel && (
                        <span className="ml-2 text-white text-[0.6rem] font-bold tracking-widest uppercase px-2 py-0.5 rounded bg-red align-middle">
                          {product.discountLabel}
                        </span>
                      )}
                    </p>
                    <p className="text-custom-sm text-gray-600 mt-1">
                      {tCartView("priceLabel")}{" "}
                      {product.originalPrice ? (
                        <>
                          <span className="line-through">
                            {product.originalPrice.toFixed(2)} zł
                          </span>{" "}
                          <span className="text-red">
                            {product.price.toFixed(2)} zł
                          </span>
                        </>
                      ) : (
                        `${product.price.toFixed(2)} zł`
                      )}
                    </p>
                  </div>
                  <div>
                    {product.originalPrice ? (
                      <p className="text-right space-x-1.5">
                        <span className="text-dark-4 line-through">
                          {(product.originalPrice * product.quantity).toFixed(
                            2,
                          )}{" "}
                          zł
                        </span>
                        <span className="text-red">
                          {(product.price * product.quantity).toFixed(2)} zł
                        </span>
                      </p>
                    ) : (
                      <p className="text-dark text-right">
                        {(product.price * product.quantity).toFixed(2)} zł
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </>
          )}

          {!!couponDiscountAmount && (
            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-1.5">
                <p className="text-sm text-gray-600">
                  {t("coupon")}{" "}
                  {couponDiscountType === DiscountType.PERCENT
                    ? `(${couponDiscountValue}%)`
                    : ""}
                </p>
                <div className="group relative flex items-center">
                  <svg
                    className="w-3.5 h-3.5 text-gray-500 cursor-help"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10A8 8 0 11 2 10a8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    />
                  </svg>
                  <span className="pointer-events-none absolute bottom-full left-1/2 mb-2 w-56 -translate-x-1/2 rounded-md bg-dark px-3 py-2 text-xs text-white opacity-0 shadow-lg transition-opacity duration-200 group-hover:opacity-100 z-10">
                    {t("couponInfo")}
                  </span>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-600 text-right">
                  - {formatPrice(couponDiscountAmount)}
                </p>
              </div>
            </div>
          )}

          {!!cartPromotionDiscountAmount && cartPromotion && (
            <div className="flex items-center justify-between pt-2">
              <p className="text-sm font-medium text-red">
                {t("cartPromotion")} ({cartPromotion.name})
              </p>
              <p className="text-sm font-medium text-red text-right">
                - {formatPrice(cartPromotionDiscountAmount)}
              </p>
            </div>
          )}

          {/* <!-- total --> */}
          <div className="flex items-center justify-between pt-5">
            <div>
              <p className="font-bold text-lg text-dark">{t("total")}</p>
            </div>
            <div>
              <p className="font-medium text-lg text-dark text-right">
                {formatPrice(discountedPrice)}
              </p>
            </div>
          </div>

          {/* <!-- checkout button --> */}
          <Link
            href="/checkout"
            className="w-full flex justify-center font-medium text-white bg-blue py-3 px-6 rounded-full ease-out duration-200 hover:bg-blue-dark mt-7.5"
          >
            {t("processToCheckout")}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;
